import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { CheckCircle, AlertTriangle, XCircle, ArrowRight, ArrowLeft, Download, Shield, FileText, Lightbulb, Share2, Mail, ThumbsUp, ThumbsDown, Send } from "lucide-react";
import AnonymityBanner from "@/components/AnonymityBanner";
import NextStepsFooter from "@/components/NextStepsFooter";
import TransitionReassurance from "@/components/TransitionReassurance";
import "@/styles/govtech.css";

// Question Flow - Based on 10 Years of Frontline Experience
const QUESTIONS = [
  {
    id: "sole_tenant",
    step: 1,
    question: "Was the person who passed away the sole tenant?",
    options: [
      { value: "yes", label: "Yes, they were the only tenant named on the tenancy" },
      { value: "no", label: "No, there was another tenant named" },
      { value: "joint", label: "They were a Joint Tenant with me" }
    ],
    insight: null
  },
  {
    id: "relationship",
    step: 2,
    question: "What was your relationship to the tenant?",
    options: [
      { value: "spouse", label: "Spouse, Civil Partner, or Cohabiting Partner" },
      { value: "child", label: "Son or Daughter" },
      { value: "parent", label: "Parent" },
      { value: "sibling", label: "Brother or Sister" },
      { value: "other", label: "Other family member or non-relative" }
    ],
    insight: null
  },
  {
    id: "residency_period",
    step: 3,
    question: "How long were you living in the property before they passed away?",
    options: [
      { value: "less_than_12", label: "Less than 12 months" },
      { value: "12_plus", label: "12 months or more" }
    ],
    insight: {
      type: "pro-tip",
      title: "10-Year Insight",
      content: "Officers look for 'Anchor Documents.' Do you have a DWP letter or a Bank Statement from exactly 12 months ago? This is the gold standard for proof."
    }
  },
  {
    id: "other_home",
    step: 4,
    question: "Do you have another home or a tenancy elsewhere?",
    options: [
      { value: "no", label: "No, this is my only home" },
      { value: "yes", label: "Yes, I have another property or tenancy" }
    ],
    insight: null
  },
  {
    id: "previous_succession",
    step: 5,
    question: "Has there been a succession on this tenancy before?",
    options: [
      { value: "no", label: "No, this would be the first succession" },
      { value: "yes", label: "Yes, there has been a previous succession" },
      { value: "not_sure", label: "I'm not sure" }
    ],
    insight: {
      type: "pro-tip",
      title: "10-Year Insight",
      content: "Check the original Tenancy Agreement. If a husband died and the wife took over, that usually counts as the 'One Succession' allowed by law."
    }
  }
];

// Logic Engine for Red/Amber/Green Status
const calculateResult = (answers) => {
  const { sole_tenant, relationship, residency_period, other_home, previous_succession } = answers;
  
  // Joint Tenant - Survivorship applies
  if (sole_tenant === "joint") {
    return {
      status: "green",
      title: "Survivorship Rights Apply",
      message: "As a Joint Tenant, you typically have automatic rights through 'Survivorship,' not Succession. The tenancy should pass to you directly.",
      recommendation: "Contact your Housing Officer to confirm the transfer and update the tenancy into your sole name."
    };
  }
  
  // RED: Automatic disqualifiers
  if (residency_period === "less_than_12") {
    return {
      status: "red",
      title: "12-Month Residency Not Met",
      message: "Under UK housing guidelines, a family member can usually take over a tenancy if they have lived at the property for at least 12 months before the tenant passed away. Your details suggest you may not meet that time threshold on your own — but you are not alone in this, and there are specialist advisors who can help you understand all of your options.",
      recommendation: "We strongly recommend speaking to a free specialist housing advisor as a next step. Shelter and Citizens Advice are two trusted services who can walk through your situation with you confidentially and explain any further options you may have."
    };
  }
  
  if (other_home === "yes") {
    return {
      status: "red",
      title: "Principal Home Requirement Not Met",
      message: "Succession rights require the property to be your 'Only or Principal Home.' Having another property or tenancy typically disqualifies you.",
      recommendation: "Seek independent housing advice urgently. Discuss your options with a housing solicitor."
    };
  }
  
  if (previous_succession === "yes") {
    return {
      status: "red",
      title: "Previous Succession Already Used",
      message: "Most UK tenancies only allow ONE legal succession. A second succession is rare and would be a 'Gift of the Landlord' at their complete discretion.",
      recommendation: "Contact your Housing Officer to discuss exceptional circumstances. Be prepared for the likelihood that succession may not be granted."
    };
  }
  
  // GREEN: Spouse/Partner with all criteria met
  if (sole_tenant === "yes" && relationship === "spouse" && residency_period === "12_plus" && other_home === "no" && (previous_succession === "no" || previous_succession === "not_sure")) {
    return {
      status: "green",
      title: "High Probability of Statutory Succession",
      message: "Based on a decade of housing logic, you meet the primary criteria for Statutory Succession. Spouses and civil partners have the strongest legal rights to succeed to a tenancy.",
      recommendation: "Contact your Housing Officer within 4 weeks to formally notify them and request the succession application form."
    };
  }
  
  // AMBER: Family members with 12+ months
  if (sole_tenant === "yes" && ["child", "parent", "sibling", "other"].includes(relationship) && residency_period === "12_plus" && other_home === "no" && (previous_succession === "no" || previous_succession === "not_sure")) {
    return {
      status: "amber",
      title: "Discretionary Succession May Apply",
      message: "You may have a Discretionary Right to succeed. The Council will require 'Meaty' evidence of your 12-month residency. Family members other than spouses typically have discretionary rather than statutory rights.",
      recommendation: "Gather comprehensive evidence immediately: utility bills, bank statements, GP records, Council Tax correspondence - all showing this address for the past 12+ months."
    };
  }
  
  // AMBER: Not sure about previous succession
  if (previous_succession === "not_sure") {
    return {
      status: "amber",
      title: "Previous Succession History Needs a Closer Look",
      message: "The history of the tenancy is unclear. Whether there has been a previous succession will significantly impact your eligibility.",
      recommendation: "Request the tenancy history from your Housing Officer before submitting an application. Check any original tenancy agreements you can find."
    };
  }
  
  // Default AMBER
  return {
    status: "amber",
    title: "A Few Details Need Reviewing",
    message: "Your situation has some areas where your housing team will want to take a closer look together with you. This is a normal next step and nothing to worry about.",
    recommendation: "Gather any evidence you have of your residency and your relationship to the tenant. A housing specialist can then help you walk through the details."
  };
};

// Evidence Checklist based on result
const getEvidenceChecklist = (status, relationship) => {
  const baseItems = [
    "Death Certificate of the tenant",
    "Proof of your identity (Passport, Driving Licence, or Birth Certificate)",
    "Proof of your relationship to the tenant (Marriage Certificate, Birth Certificate)"
  ];
  
  const residencyItems = [
    "4 Utility bills from the last 12 months (Gas, Electric, Water)",
    "Bank statements showing this address",
    "Council Tax correspondence in your name or jointly",
    "DWP or HMRC letters to this address",
    "GP registration confirmation at this address",
    "Electoral roll registration evidence"
  ];
  
  if (status === "green" && relationship === "spouse") {
    return [...baseItems, "Evidence of cohabitation (joint accounts, correspondence)"];
  }
  
  return [...baseItems, ...residencyItems];
};

// Generate PDF
const generatePDF = (answers, result) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let yPos = 20;
  
  // Helper function for text wrapping
  const addWrappedText = (text, x, y, maxWidth, lineHeight = 7) => {
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return y + (lines.length * lineHeight);
  };
  
  // Header
  doc.setFillColor(0, 112, 60); // Verification Green
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("Tenures.co.uk", margin, 25);
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("SUCCESSION RIGHTS GUIDANCE SUMMARY", margin, 35);
  
  yPos = 55;
  
  // Authority Stamp
  doc.setFillColor(244, 244, 244);
  doc.rect(margin, yPos, contentWidth, 25, 'F');
  doc.setDrawColor(0, 112, 60);
  doc.setLineWidth(1);
  doc.line(margin, yPos, margin, yPos + 25);
  
  doc.setTextColor(51, 51, 51);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("VERIFIED LOGIC", margin + 5, yPos + 10);
  doc.setFont("helvetica", "normal");
  doc.text("Derived from 10 Years of Frontline Housing Management Experience", margin + 5, yPos + 18);
  
  yPos += 35;
  
  // Status Badge
  const statusColors = {
    green: { bg: [204, 226, 216], text: [0, 112, 60], label: "LOOKING POSITIVE" },
    amber: { bg: [252, 214, 195], text: [110, 54, 25], label: "A FEW DETAILS TO CHECK" },
    red: { bg: [246, 215, 210], text: [148, 37, 20], label: "SPECIALIST ADVICE RECOMMENDED" }
  };
  
  const statusStyle = statusColors[result.status];
  doc.setFillColor(...statusStyle.bg);
  doc.rect(margin, yPos, contentWidth, 15, 'F');
  doc.setDrawColor(...statusStyle.text);
  doc.setLineWidth(1);
  doc.line(margin, yPos, margin, yPos + 15);
  
  doc.setTextColor(...statusStyle.text);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(statusStyle.label, margin + 5, yPos + 10);
  
  yPos += 25;
  
  // Result Title
  doc.setTextColor(51, 51, 51);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  yPos = addWrappedText(result.title, margin, yPos, contentWidth, 8);
  
  yPos += 5;
  
  // Result Message
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  yPos = addWrappedText(result.message, margin, yPos, contentWidth);
  
  yPos += 10;
  
  // Recommendation
  doc.setFillColor(244, 244, 244);
  doc.rect(margin, yPos, contentWidth, 25, 'F');
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("RECOMMENDED ACTION:", margin + 5, yPos + 8);
  doc.setFont("helvetica", "normal");
  yPos = addWrappedText(result.recommendation, margin + 5, yPos + 15, contentWidth - 10, 6);
  
  yPos += 20;
  
  // Evidence Checklist
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Evidence Checklist", margin, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const checklist = getEvidenceChecklist(result.status, answers.relationship);
  
  checklist.forEach(item => {
    doc.rect(margin, yPos - 3, 4, 4);
    yPos = addWrappedText(item, margin + 8, yPos, contentWidth - 10, 6);
    yPos += 2;
  });
  
  // New page for Cover Letter
  doc.addPage();
  yPos = 20;
  
  // Cover Letter Header
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Template Letter to Housing Officer", margin, yPos);
  yPos += 15;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  
  const letterContent = `Dear Lettings Manager,

RE: Succession Application Following Death of Tenant

I am writing to formally notify you of the death of the tenant at the above property and to request information about the succession process.

My relationship to the deceased tenant: ${answers.relationship?.replace('_', ' ').toUpperCase() || 'N/A'}
Length of residence at the property: ${answers.residency_period === '12_plus' ? '12 months or more' : 'Less than 12 months'}

I understand that I may need to provide evidence of my residency at this property and my relationship to the tenant. I am in the process of gathering the following documentation:

- Death Certificate
- Proof of identity
- Proof of relationship
- Evidence of 12-month residency (utility bills, bank statements, official correspondence)

I would be grateful if you could:
1. Confirm the process for applying for succession
2. Provide me with the relevant application form
3. Advise of any specific evidence requirements

I look forward to hearing from you at your earliest convenience.

Yours faithfully,

[Your Name]
[Property Address]
[Date]`;

  const letterLines = doc.splitTextToSize(letterContent, contentWidth);
  doc.text(letterLines, margin, yPos);
  
  // Footer on all pages
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Generated by Tenures.co.uk | ${new Date().toLocaleDateString('en-GB')} | Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
    doc.text(
      "This assessment is for guidance only. Final decisions are made by your Housing Provider.",
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 5,
      { align: 'center' }
    );
  }
  
  doc.save("Tenures_Succession_Assessment.pdf");
};

// Tenures Logo Component
const TenuresLogo = () => (
  <span className="text-xl font-bold text-[#333333]">
    Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
  </span>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    green: { icon: CheckCircle, label: "Looking Positive", className: "status-green" },
    amber: { icon: AlertTriangle, label: "A Few Details to Check", className: "status-amber" },
    red: { icon: XCircle, label: "Specialist Advice Recommended", className: "status-red" }
  };
  
  const { icon: Icon, label, className } = config[status];
  
  return (
    <div className={`status-badge ${className}`}>
      <Icon size={18} />
      <span>{label}</span>
    </div>
  );
};

export default function SuccessionTriage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Email capture state
  const [emailCapture, setEmailCapture] = useState({ email: "", postcode: "" });
  const [emailSent, setEmailSent] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  
  // Feedback state
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  
  const currentQuestion = QUESTIONS[currentStep];
  const progress = ((currentStep + 1) / QUESTIONS.length) * 100;
  
  // Track anonymous session
  useEffect(() => {
    // Anonymous analytics - just track that someone started the triage
    console.log(`[Analytics] Session ${sessionId}: Succession Triage Started`);
  }, [sessionId]);
  
  const handleAnswer = (value) => {
    setAnswers({ ...answers, [currentQuestion.id]: value });
  };
  
  const handleNext = () => {
    if (!answers[currentQuestion.id]) return;
    
    if (currentStep < QUESTIONS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Calculate result
      const triageResult = calculateResult(answers);
      setResult(triageResult);
      
      // Anonymous analytics
      console.log(`[Analytics] Session ${sessionId}: Triage Complete - Status: ${triageResult.status}`);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleDownloadPDF = () => {
    generatePDF(answers, result);
    console.log(`[Analytics] Session ${sessionId}: PDF Downloaded`);
    setPdfDownloaded(true);
  };
  
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!emailCapture.email) return;
    
    // Store lead data
    const leadData = {
      email: emailCapture.email,
      postcode: emailCapture.postcode,
      tool: "succession",
      result: result?.status,
      timestamp: new Date().toISOString()
    };
    
    // Save to localStorage for analytics
    const existingLeads = JSON.parse(localStorage.getItem('tenures_resident_leads') || '[]');
    existingLeads.push(leadData);
    localStorage.setItem('tenures_resident_leads', JSON.stringify(existingLeads));
    
    console.log(`[Lead Captured] Succession Triage:`, leadData);
    setEmailSent(true);
    
    // Also download the PDF
    generatePDF(answers, result);
    setPdfDownloaded(true);
  };
  
  const handleFeedback = (isPositive) => {
    const feedbackData = {
      tool: "succession",
      helpful: isPositive,
      result: result?.status,
      timestamp: new Date().toISOString()
    };
    
    // Save to localStorage
    const existingFeedback = JSON.parse(localStorage.getItem('tenures_feedback') || '[]');
    existingFeedback.push(feedbackData);
    localStorage.setItem('tenures_feedback', JSON.stringify(existingFeedback));
    
    console.log(`[Feedback] Succession Triage:`, feedbackData);
    setFeedbackGiven(true);
  };
  
  const handleRestart = () => {
    setCurrentStep(0);
    setAnswers({});
    setResult(null);
  };
  
  const handleShare = () => {
    const shareText = "I just used TenURes to check my succession rights - it's a free tool for social housing tenants. Takes 2 minutes and gives you a professional PDF. Check it out:";
    const shareUrl = window.location.origin + "/succession-triage";
    
    if (navigator.share) {
      navigator.share({
        title: 'TenURes - Succession Rights Checker',
        text: shareText,
        url: shareUrl
      });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/resident')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft size={20} />
            <span>Back to Portal</span>
          </button>
          <TenuresLogo />
        </div>
      </nav>
      
      {/* Progress Bar */}
      {!result && (
        <div className="max-w-4xl mx-auto px-4 pt-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-500">Succession Guidance Checker</span>
            <span className="text-sm text-gray-500">Step {currentStep + 1} of {QUESTIONS.length}</span>
          </div>
          <div className="progress-container">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-sm text-gray-500 text-center">About 2 minutes to complete</p>
          <div className="mt-4">
            <AnonymityBanner />
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        {/* Authority Stamp */}
        <div className="authority-stamp mb-8">
          <div className="authority-stamp-icon">
            <Shield size={20} />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Designed with a Decade of Frontline Housing Expertise</p>
            <p className="text-sm text-gray-600">Professional-grade triage logic trusted by Housing Officers</p>
          </div>
        </div>
        
        {!result ? (
          /* Question View */
          <div className="govtech-card">
            <TransitionReassurance step={currentStep} />
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center">
                <FileText className="text-[#00703C]" size={20} />
              </div>
              <h1 className="govtech-heading-m">{currentQuestion.question}</h1>
            </div>
            
            {/* Pro Tip */}
            {currentQuestion.insight && (
              <div className="pro-tip mb-6">
                <div className="flex items-center gap-2 mb-1">
                  <Lightbulb size={16} className="text-[#F47738]" />
                  <span className="pro-tip-label">{currentQuestion.insight.title}</span>
                </div>
                <p className="text-gray-700 text-sm">{currentQuestion.insight.content}</p>
              </div>
            )}
            
            {/* Options */}
            <div className="govtech-radio-group mb-8">
              {currentQuestion.options.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleAnswer(option.value)}
                  className={`govtech-radio-item ${answers[currentQuestion.id] === option.value ? 'selected' : ''}`}
                  data-testid={`option-${option.value}`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    answers[currentQuestion.id] === option.value 
                      ? 'border-[#00703C] bg-[#00703C]' 
                      : 'border-gray-400'
                  }`}>
                    {answers[currentQuestion.id] === option.value && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <span className="text-gray-900">{option.label}</span>
                </div>
              ))}
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex gap-4">
              {currentStep > 0 && (
                <button
                  onClick={handleBack}
                  className="govtech-btn govtech-btn-secondary flex-1"
                  data-testid="back-btn"
                >
                  <ArrowLeft size={18} className="mr-2" />
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={!answers[currentQuestion.id]}
                className={`govtech-btn govtech-btn-primary flex-1 ${!answers[currentQuestion.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="next-btn"
              >
                {currentStep < QUESTIONS.length - 1 ? (
                  <>
                    Continue
                    <ArrowRight size={18} className="ml-2" />
                  </>
                ) : (
                  "See My Guidance Summary"
                )}
              </button>
            </div>
          </div>
        ) : (
          /* Result View */
          <div className="govtech-card">
            <AnonymityBanner variant="full" />
            <div className="text-center mb-6">
              <h1 className="govtech-heading-m mb-4">Your Succession Guidance Summary</h1>
              <StatusBadge status={result.status} />
            </div>
            
            <div className="govtech-card govtech-card-highlight mb-6">
              <h2 className="font-bold text-gray-900 mb-2">{result.title}</h2>
              <p className="text-gray-700">{result.message}</p>
            </div>
            
            <div className="bg-gray-50 p-4 mb-6">
              <h3 className="font-bold text-gray-900 mb-2">Recommended Action</h3>
              <p className="text-gray-700">{result.recommendation}</p>
            </div>
            
            {/* Evidence Checklist Preview */}
            <div className="mb-8">
              <h3 className="font-bold text-gray-900 mb-3">Evidence Checklist</h3>
              <ul className="space-y-2">
                {getEvidenceChecklist(result.status, answers.relationship).slice(0, 5).map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-gray-700">
                    <div className="w-4 h-4 border border-gray-400 mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-500 mt-2">Download the full PDF for complete checklist and cover letter template.</p>
            </div>

            {/* Next Steps / Call-Deflation */}
            <NextStepsFooter reviewDays={28} teamName="local housing team" />

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              {/* Email Capture Option */}
              {!showEmailForm && !emailSent && (
                <button
                  onClick={() => setShowEmailForm(true)}
                  className="govtech-btn govtech-btn-primary w-full"
                  data-testid="email-pdf-btn"
                >
                  <Mail size={18} className="mr-2" />
                  Email Me My Report
                </button>
              )}
              
              {showEmailForm && !emailSent && (
                <form onSubmit={handleEmailSubmit} className="bg-gray-50 p-4 space-y-3">
                  <p className="text-sm font-medium text-[#333333]">Get your report sent to your inbox:</p>
                  <input
                    type="email"
                    required
                    placeholder="your@email.com"
                    className="govtech-input"
                    value={emailCapture.email}
                    onChange={(e) => setEmailCapture({...emailCapture, email: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Your postcode (optional - helps us help your area)"
                    className="govtech-input"
                    value={emailCapture.postcode}
                    onChange={(e) => setEmailCapture({...emailCapture, postcode: e.target.value})}
                  />
                  <div className="flex gap-2">
                    <button type="submit" className="govtech-btn govtech-btn-primary flex-1">
                      <Send size={16} className="mr-2" />
                      Send & Download
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setShowEmailForm(false)}
                      className="govtech-btn govtech-btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">We'll only email you your report. No spam, ever.</p>
                </form>
              )}
              
              {emailSent && (
                <div className="bg-[#00703C] bg-opacity-10 border border-[#00703C] p-4 text-center">
                  <CheckCircle className="text-[#00703C] mx-auto mb-2" size={24} />
                  <p className="text-[#00703C] font-medium">Report sent to {emailCapture.email}</p>
                  <p className="text-sm text-gray-600 mt-1">Check your inbox (and spam folder)</p>
                </div>
              )}
              
              {!showEmailForm && (
                <button
                  onClick={handleDownloadPDF}
                  className={`govtech-btn ${emailSent ? 'govtech-btn-secondary' : 'govtech-btn-secondary'} w-full`}
                  data-testid="download-pdf-btn"
                >
                  <Download size={18} className="mr-2" />
                  {emailSent ? 'Download Again' : 'Just Download PDF'}
                </button>
              )}
              
              {/* Feedback Section - Shows after PDF download */}
              {pdfDownloaded && !feedbackGiven && (
                <div className="bg-gray-50 p-4 mt-2">
                  <p className="text-sm font-medium text-[#333333] text-center mb-3">Was this tool helpful?</p>
                  <div className="flex justify-center gap-4">
                    <button
                      onClick={() => handleFeedback(true)}
                      className="flex items-center gap-2 px-6 py-2 bg-white border border-gray-200 hover:border-[#00703C] hover:bg-[#00703C] hover:bg-opacity-5 transition-all"
                    >
                      <ThumbsUp size={18} className="text-[#00703C]" />
                      <span>Yes</span>
                    </button>
                    <button
                      onClick={() => handleFeedback(false)}
                      className="flex items-center gap-2 px-6 py-2 bg-white border border-gray-200 hover:border-red-400 hover:bg-red-50 transition-all"
                    >
                      <ThumbsDown size={18} className="text-gray-500" />
                      <span>No</span>
                    </button>
                  </div>
                </div>
              )}
              
              {feedbackGiven && (
                <div className="bg-[#00703C] bg-opacity-5 p-3 text-center">
                  <p className="text-sm text-[#00703C]">Thank you for your feedback!</p>
                </div>
              )}
              
              <button
                onClick={handleShare}
                className="govtech-btn govtech-btn-secondary w-full"
                data-testid="share-btn"
              >
                <Share2 size={18} className="mr-2" />
                Share With a Neighbour
              </button>
              
              <button
                onClick={handleRestart}
                className="text-[#00703C] font-medium text-sm hover:underline text-center"
                data-testid="restart-btn"
              >
                Start a New Guidance Checker
              </button>
            </div>
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            This Guidance Checker is here to help you understand your housing options.
            It is not legal advice. Final decisions are made by your housing team.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            © {new Date().getFullYear()} Ten<span className="text-[#00703C] font-semibold">UR</span>es.co.uk — Professional Housing Infrastructure
          </p>
        </div>
      </footer>
    </div>
  );
}
